import React from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useIntl } from 'react-intl';
import { COLORS } from '../constants';
import { GROUP_OPTIONS, GroupBy } from '../utils/grouping';

type Props = {
  value: GroupBy;
  onChange: (next: GroupBy) => void;
};

// Single-select "Group by: <dimension> ▾" control. antd Dropdown with a
// selectable menu (radio semantics via selectedKeys) — replaces the old binary
// "Group by area" checkbox. The menu portals to <body>, so it isn't clipped by
// the sticky header/corner cell.
const GroupByDropdown: React.FC<Props> = ({ value, onChange }) => {
  const intl = useIntl();

  const labelFor = (key: GroupBy): string => {
    const opt = GROUP_OPTIONS.find((o) => o.key === key) ?? GROUP_OPTIONS[0];
    return intl.formatMessage({
      id: opt.labelId,
      defaultMessage: opt.defaultLabel,
    });
  };

  const items: MenuProps['items'] = GROUP_OPTIONS.map((o) => ({
    key: o.key,
    label: intl.formatMessage({
      id: o.labelId,
      defaultMessage: o.defaultLabel,
    }),
  }));

  const groupByText = intl.formatMessage({
    id: 'fleetadmin.timeline.groupBy',
    defaultMessage: 'Group by',
  });

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items,
        selectable: true,
        selectedKeys: [value],
        onSelect: ({ key }) => onChange(key as GroupBy),
      }}
    >
      <button
        type="button"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 6,
          width: '100%',
          padding: '4px 8px',
          border: `1px solid ${COLORS.plateColumnBorder}`,
          borderRadius: 6,
          background: COLORS.headerBg,
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 500,
          color: COLORS.primaryText,
          lineHeight: 1.2,
          boxSizing: 'border-box',
        }}
      >
        <span
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {groupByText}: {labelFor(value)}
        </span>
        {/* Chevron — same SVG/colour as the group-row chevron in PlateColumn. */}
        <svg
          aria-hidden
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          style={{ flex: '0 0 auto' }}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="#314158"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </Dropdown>
  );
};

export default GroupByDropdown;
